import type { FastifyInstance } from 'fastify'
import type { LiveController } from './live.controller.js'
import type { LiveNavigationController } from './liveNavigation.controller.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'

export async function liveRoutes(
  app: FastifyInstance, 
  controller: LiveController, 
  navController: LiveNavigationController
) {
  // ==========================================
  // LEGACY - Friends & Clans Locations
  // ==========================================
  
  app.get('/friends', {
    handler: controller.getFriendLocations.bind(controller)
  })

  app.get('/clans/:clanId', {
    handler: controller.getClanLocations.bind(controller)
  })

  // ==========================================
  // SESSION MANAGEMENT
  // ==========================================

  /**
   * Create new live navigation session
   * POST /api/live/sessions
   */
  app.post('/sessions', {
    preHandler: [authMiddleware],
    handler: navController.createSession.bind(navController)
  })

  /**
   * Get live session by ID
   * GET /api/live/sessions/:sessionId
   */
  app.get('/sessions/:sessionId', {
    preHandler: [authMiddleware],
    handler: navController.getSession.bind(navController)
  })

  /**
   * List active sessions (public + user's)
   * GET /api/live/sessions
   */
  app.get('/sessions', {
    preHandler: [authMiddleware],
    handler: navController.listSessions.bind(navController)
  })

  /**
   * End live session
   * POST /api/live/sessions/:sessionId/end
   */
  app.post('/sessions/:sessionId/end', {
    preHandler: [authMiddleware],
    handler: navController.endSession.bind(navController)
  })

  // ==========================================
  // PARTICIPANT MANAGEMENT
  // ==========================================

  /**
   * Join live session
   * POST /api/live/sessions/:sessionId/join
   */
  app.post('/sessions/:sessionId/join', {
    preHandler: [authMiddleware],
    handler: navController.joinSession.bind(navController)
  })

  /**
   * Leave live session
   * POST /api/live/sessions/:sessionId/leave
   */
  app.post('/sessions/:sessionId/leave', {
    preHandler: [authMiddleware],
    handler: navController.leaveSession.bind(navController)
  })

  /**
   * Update participant position
   * POST /api/live/sessions/:sessionId/position
   */
  app.post('/sessions/:sessionId/position', {
    preHandler: [authMiddleware],
    handler: navController.updatePosition.bind(navController)
  })

  /**
   * Update participant role
   * PATCH /api/live/sessions/:sessionId/participants/:userId/role
   */
  app.patch('/sessions/:sessionId/participants/:userId/role', {
    preHandler: [authMiddleware],
    handler: navController.updateParticipantRole.bind(navController)
  })

  /**
   * Remove participant
   * DELETE /api/live/sessions/:sessionId/participants/:userId
   */
  app.delete('/sessions/:sessionId/participants/:userId', {
    preHandler: [authMiddleware],
    handler: navController.removeParticipant.bind(navController)
  })

  // ==========================================
  // CHAT
  // ==========================================

  /**
   * Send chat message
   * POST /api/live/sessions/:sessionId/chat
   */
  app.post('/sessions/:sessionId/chat', {
    preHandler: [authMiddleware],
    handler: navController.sendChatMessage.bind(navController)
  })

  /**
   * Get chat history
   * GET /api/live/sessions/:sessionId/chat
   */
  app.get('/sessions/:sessionId/chat', {
    preHandler: [authMiddleware],
    handler: navController.getChatHistory.bind(navController)
  })

  // ==========================================
  // SAFETY & ALERTS
  // ==========================================

  /**
   * Send emergency alert
   * POST /api/live/sessions/:sessionId/emergency
   */
  app.post('/sessions/:sessionId/emergency', {
    preHandler: [authMiddleware],
    handler: navController.sendEmergencyAlert.bind(navController)
  })

  /**
   * Clear emergency alert
   * DELETE /api/live/sessions/:sessionId/emergency
   */
  app.delete('/sessions/:sessionId/emergency', {
    preHandler: [authMiddleware],
    handler: navController.clearEmergencyAlert.bind(navController)
  })

  // ==========================================
  // TELEMETRY & TRACK DAYS
  // ==========================================

    /**
   * Record telemetry data
   * POST /api/live/sessions/:sessionId/telemetry
   */
  app.post('/sessions/:sessionId/telemetry', {
    preHandler: [authMiddleware],
    handler: navController.recordTelemetry.bind(navController)
  })

  /**
   * Complete lap
   * POST /api/live/sessions/:sessionId/laps
   */
  app.post('/sessions/:sessionId/laps', {
    preHandler: [authMiddleware],
    handler: navController.completeLap.bind(navController)
  })

  /**
   * Get leaderboard
   * GET /api/live/sessions/:sessionId/leaderboard
   */
  app.get('/sessions/:sessionId/leaderboard', {
    preHandler: [authMiddleware],
    handler: navController.getLeaderboard.bind(navController)
  })

  // ==========================================
  // REPLAY & EXPORT
  // ==========================================

  /**
   * Get session replay
   * GET /api/live/sessions/:sessionId/replay
   */
  app.get('/sessions/:sessionId/replay', {
    preHandler: [authMiddleware],
    handler: navController.getReplay.bind(navController)
  })

  /**
   * Export session data (GPX, CSV, JSON)
   * GET /api/live/sessions/:sessionId/export
   */
  app.get('/sessions/:sessionId/export', {
    preHandler: [authMiddleware],
    handler: navController.exportSession.bind(navController)
  })

  // ==========================================
  // STATS
  // ==========================================

  /**
   * Get session stats
   * GET /api/live/sessions/:sessionId/stats
   */
  app.get('/sessions/:sessionId/stats', {
    preHandler: [authMiddleware],
    handler: navController.getSessionStats.bind(navController)
  })
}
